/**
 * What the weapons weigh, where they sit, and what they cost in drag.
 *
 * ## Why an armed aeroplane needs this
 *
 * Until M7 the Vandal's ordnance was massless and frictionless. Four missiles and five hundred
 * rounds of 20 mm are 391 kg — a little over three per cent of a 12 000 kg fighter — hung mostly at
 * the wingtips and in the nose, and expending them is one of the few things a pilot does that
 * changes the aeroplane's *character* rather than merely its state. Firing the gun dry moves the
 * centre of gravity aft by centimetres and takes 51 kg off the nose; shooting off all four rounds
 * takes 340 kg off the wingtips, cuts the roll inertia, and gives back the parasite drag they were
 * costing. None of that was modelled, so a Vandal at bingo fuel and Winchester flew exactly like
 * one that had not fired a shot.
 *
 * ## Loaded is the reference, not empty
 *
 * The config's `maxTakeoffKg` and its inertia tensor describe the aeroplane **as loaded**, which is
 * how published figures are quoted and what every existing validation gate was written against. So
 * the stores are modelled as a *subtraction*: a full aircraft is exactly what it was before this
 * file existed, and expending stores removes their mass, their moment and their drag. That ordering
 * is deliberate — the alternative, adding stores on top of the published weight, would have made
 * every gate in `vandal.gates.test.ts` wrong on the day it landed, and would have been indefensible
 * besides, because the published weight already includes them.
 *
 * ## What it produces
 *
 * A {@link StoresLoad}: the delta from the fully-loaded reference. The flight model applies it in
 * three places — mass, inertia (parallel axis), and the parasite drag area — plus the pitching
 * moment that a centre of gravity displaced from the body origin produces under g.
 *
 * Aircraft-agnostic by construction: everything here comes from the aircraft's own JSON, and an
 * aeroplane with no `weapons` block gets a zero load and never notices. Non-negotiable #8.
 *
 * `updateFixed(dt)` / `updateFrame(alpha)`: neither. Pure arithmetic, called by the weapon system
 * when a count changes rather than every tick — see {@link StoresTracker}.
 */

import type { CannonConfig, MissileConfig, WeaponsConfig } from './config.js';

/**
 * The difference between what is aboard now and a full load.
 *
 * Every field is a **delta from fully loaded**, so a full aircraft is all zeros and nothing about
 * it changes. See the note above on why the reference is loaded rather than empty.
 */
export interface StoresLoad {
  /** Mass expended [kg], ≥ 0. Subtracted from the aircraft's mass. */
  expendedKg: number;
  /**
   * First moment of the expended mass about the body origin [kg·m], body axes (x forward, y
   * right, z down). Divided by the current mass, this is how far the centre of gravity moved.
   */
  expendedMomentKgM: readonly [number, number, number];
  /** Parasite drag area given back by what is no longer hanging outside [m²], ≥ 0. */
  expendedDragAreaM2: number;
  /** Parallel-axis inertia the expended mass took with it [kg·m²]. */
  expendedIxx: number;
  expendedIyy: number;
  expendedIzz: number;
  expendedIxz: number;
}

/** A load of nothing — a full aircraft, or one with no weapons at all. */
export const FULL_STORES: StoresLoad = {
  expendedKg: 0,
  expendedMomentKgM: [0, 0, 0],
  expendedDragAreaM2: 0,
  expendedIxx: 0,
  expendedIyy: 0,
  expendedIzz: 0,
  expendedIxz: 0,
};

/** What is still aboard. */
export interface StoresRemaining {
  /** Missiles still on the rails. */
  missiles: number;
  /** Rounds still in the drum. */
  rounds: number;
}

/**
 * The mass of a full weapons load [kg] — what `maxTakeoffKg` already includes.
 *
 * Useful on its own: it is what the stores page would show, and it is the number that says whether
 * modelling them is worth doing at all. For the Vandal it is 391 kg against a 12 000 kg aeroplane.
 */
export function fullStoresMassKg(weapons: WeaponsConfig | undefined): number {
  if (weapons === undefined) return 0;
  return missileMassKg(weapons.missile) + cannonMassKg(weapons.cannon);
}

function missileMassKg(missile: MissileConfig | undefined): number {
  return missile === undefined ? 0 : missile.count * missile.massKg;
}

function cannonMassKg(cannon: CannonConfig | undefined): number {
  return cannon === undefined ? 0 : cannon.capacity * cannon.roundMassKg;
}

/**
 * Work out what has been expended, and what that does to the aeroplane.
 *
 * Allocation-free into `out`, because the weapon system calls it whenever a round leaves and that
 * is a hundred times a second with the trigger held down.
 *
 * ## Which missile leaves first matters, and this knows
 *
 * `WeaponSystem.launch` fires the stations in order, and the stations are not symmetric pairs in
 * sequence — so a battery with three rounds left is genuinely lopsided, and the lateral moment
 * below is what makes a half-empty aeroplane roll off. The station list is walked from the end,
 * matching the same `stations.length - remaining` indexing the launcher uses; get that backwards
 * and the aeroplane rolls the wrong way, which is the sort of thing nobody notices until it is
 * shipped.
 */
export function computeStoresLoad(
  weapons: WeaponsConfig | undefined,
  remaining: StoresRemaining,
  out: {
    expendedKg: number;
    expendedMomentKgM: [number, number, number];
    expendedDragAreaM2: number;
    expendedIxx: number;
    expendedIyy: number;
    expendedIzz: number;
    expendedIxz: number;
  },
): StoresLoad {
  out.expendedKg = 0;
  out.expendedMomentKgM[0] = 0;
  out.expendedMomentKgM[1] = 0;
  out.expendedMomentKgM[2] = 0;
  out.expendedDragAreaM2 = 0;
  out.expendedIxx = 0;
  out.expendedIyy = 0;
  out.expendedIzz = 0;
  out.expendedIxz = 0;
  if (weapons === undefined) return out;

  const missile = weapons.missile;
  if (missile !== undefined) {
    const stations = missile.stationsM;
    const gone = Math.max(0, Math.min(missile.count, missile.count - remaining.missiles));
    // The launcher takes `stations[stations.length - remaining]`, so the first `gone` stations in
    // list order are the empty ones.
    for (let i = 0; i < gone && i < stations.length; i++) {
      addPointMass(out, missile.massKg, stations[i], missile.railDragAreaM2 ?? 0);
    }
  }

  const cannon = weapons.cannon;
  if (cannon !== undefined) {
    const spent = Math.max(0, Math.min(cannon.capacity, cannon.capacity - remaining.rounds));
    // The drum, not the muzzle: the rounds sit where they are stowed, and on a nose-mounted gun
    // that is well forward of the centre of gravity, which is why shooting it dry trims aft.
    const at = cannon.ammunitionPositionM ?? cannon.muzzlePositionM;
    // Ammunition is internal, so it costs no drag — only its mass and its arm.
    addPointMass(out, spent * cannon.roundMassKg, at, 0);
  }

  return out;
}

function addPointMass(
  out: {
    expendedKg: number;
    expendedMomentKgM: [number, number, number];
    expendedDragAreaM2: number;
    expendedIxx: number;
    expendedIyy: number;
    expendedIzz: number;
    expendedIxz: number;
  },
  massKg: number,
  at: readonly [number, number, number],
  dragAreaM2: number,
): void {
  if (massKg <= 0) return;
  const [x, y, z] = at;
  out.expendedKg += massKg;
  out.expendedMomentKgM[0] += massKg * x;
  out.expendedMomentKgM[1] += massKg * y;
  out.expendedMomentKgM[2] += massKg * z;
  out.expendedDragAreaM2 += dragAreaM2;
  // Parallel axis, about the body origin. The store's own inertia about its centre is negligible
  // beside `m·r²` at these arms — a missile is 3 m long and sits 5 m out.
  out.expendedIxx += massKg * (y * y + z * z);
  out.expendedIyy += massKg * (x * x + z * z);
  out.expendedIzz += massKg * (x * x + y * y);
  out.expendedIxz += massKg * x * z;
}

/** A reusable {@link StoresLoad} the frame path can write into. */
export function makeStoresLoad(): StoresLoad & {
  expendedMomentKgM: [number, number, number];
} {
  return {
    expendedKg: 0,
    expendedMomentKgM: [0, 0, 0],
    expendedDragAreaM2: 0,
    expendedIxx: 0,
    expendedIyy: 0,
    expendedIzz: 0,
    expendedIxz: 0,
  };
}
